import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { FadeUp } from "@/components/Reveal";
import { useLocation, useNavigate } from "react-router-dom";
import { API, FOURTH_TRIMESTER_SLUG, FOURTH_TRIMESTER_DATES } from "@/data/site";
import { OwnerSignIn } from "@/components/OwnerSignIn";

export default function Appointments() {
  const location = useLocation();
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(location.state?.user ? true : null);
  const [bookings, setBookings] = useState([]);
  const [selected, setSelected] = useState(new Date());
  const [month, setMonth] = useState(new Date());

  useEffect(() => {
    axios
      .get(`${API}/bookings`, { withCredentials: true })
      .then((res) => {
        setBookings(res.data);
        setAuthed(true);
      })
      .catch(() => setAuthed(false));
  }, []);

  const removeBooking = async (reference) => {
    const isCourse = bookings.find((b) => b.reference === reference)?.service_slug === FOURTH_TRIMESTER_SLUG;
    const msg = isCourse
      ? `Remove course sign-up ${reference}? She will be removed from all 6 Sundays.`
      : `Remove booking ${reference}? This frees the time for others.`;
    if (!window.confirm(msg)) return;
    await axios.delete(`${API}/bookings/${reference}`, { withCredentials: true });
    setBookings((prev) => prev.filter((b) => b.reference !== reference));
  };

  const signOut = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    navigate("/appointments", { replace: true, state: {} });
    setAuthed(false);
  };

  const byDate = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      const dates = b.service_slug === FOURTH_TRIMESTER_SLUG ? FOURTH_TRIMESTER_DATES : [b.date];
      for (const d of dates) (map[d] = map[d] || []).push(b);
    }
    return map;
  }, [bookings]);

  const bookedDates = useMemo(
    () => Object.keys(byDate).map((d) => parseISO(d + "T00:00:00")),
    [byDate]
  );

  const selectedKey = selected ? format(selected, "yyyy-MM-dd") : null;
  const dayBookings = (selectedKey && byDate[selectedKey]) || [];

  if (authed === null) return <main className="min-h-screen bg-ivory" data-testid="appointments-loading" />;
  if (authed === false) return <OwnerSignIn denied={location.state?.denied} />;

  return (
    <main className="min-h-screen bg-ivory pt-32 pb-24 lg:pt-40" data-testid="appointments-page">
      <div className="mx-auto max-w-5xl px-6">
        <FadeUp>
          <div className="flex items-center justify-between gap-4">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sienna">Your calendar</p>
            <button onClick={signOut} data-testid="owner-signout-btn" className="text-xs uppercase tracking-[0.2em] text-cocoa underline-offset-4 transition-colors hover:text-sienna hover:underline">
              Sign out
            </button>
          </div>
          <h1 className="mt-5 font-serif text-5xl font-light leading-tight text-espresso lg:text-6xl">
            Appointments & <span className="italic text-sienna">availability</span>
          </h1>
          <p className="mt-4 max-w-xl text-base text-cocoa">
            Days marked in sienna hold bookings. Select a day to see its appointments —
            visitors booking online can only choose times that are still free.
          </p>
        </FadeUp>

        <div className="mt-12 grid grid-cols-1 gap-12 lg:grid-cols-12">
          <FadeUp className="lg:col-span-6">
            <div className="rounded-2xl border border-espresso/15 bg-cream p-6" data-testid="appointments-calendar">
              <Calendar
                mode="single"
                selected={selected}
                onSelect={(d) => d && setSelected(d)}
                month={month}
                onMonthChange={setMonth}
                modifiers={{ booked: bookedDates }}
                modifiersClassNames={{ booked: "day-booked" }}
              />
              <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-cocoa/70">
                <span className="inline-block h-2 w-2 rounded-full bg-sienna" /> Booked day
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.15} className="lg:col-span-6">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sienna" data-testid="appointments-day-label">
              {selected ? format(selected, "EEEE, MMMM d, yyyy") : "Select a day"}
            </p>
            <div className="mt-6 space-y-4" data-testid="appointments-day-list">
              {dayBookings.length === 0 && (
                <p className="rounded-xl border border-espresso/15 bg-cream p-6 text-sm text-cocoa" data-testid="appointments-empty">
                  Nothing booked this day — fully open for new appointments.
                </p>
              )}
              {dayBookings.map((b) => (
                <div key={b.id} className="rounded-xl border border-espresso/15 bg-cream p-6" data-testid={`appointment-${b.reference}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-serif text-xl text-espresso">{b.service_title}</p>
                      <p className="mt-1 text-sm text-cocoa">
                        {b.service_slug === FOURTH_TRIMESTER_SLUG && selectedKey && `Week ${FOURTH_TRIMESTER_DATES.indexOf(selectedKey) + 1} of 6 · `}
                        {b.time} · {b.name}
                      </p>
                      <p className="mt-1 text-sm text-cocoa/70">{b.email}{b.phone ? ` · ${b.phone}` : ""}</p>
                      {b.notes && <p className="mt-3 text-sm italic leading-relaxed text-cocoa/80">"{b.notes}"</p>}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-3">
                      <span className="rounded-full border border-sienna/40 px-3 py-1 text-xs font-medium uppercase tracking-[0.12em] text-sienna">
                        {b.reference}
                      </span>
                      <button
                        onClick={() => removeBooking(b.reference)}
                        data-testid={`remove-booking-${b.reference}`}
                        className="text-xs uppercase tracking-[0.15em] text-cocoa/70 underline-offset-4 transition-colors hover:text-sienna hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </div>
    </main>
  );
}
